<template>
  <div
    v-if="!screenSizeIsTooSmall"
    class="row editor"
  >
    <div class="menu editor-column col-md-3 col-lg-3 col-xl-3">
      <div
        v-for="section in internalDragElements"
        :key="section.title"
      >
        <h4>
          {{ section.title }}
        </h4>
        <drag-container
          @onDrop="onDrop"
          groupName="menu-default"
          :shouldRemoveOnDrop="false"
          :shouldAcceptDrops="false"
          :dragClass="dragClass"
          :showTabs="showTabs"
          :collectionDrag="cleanSectionItems(section.items, internalStructure)"
          :collection="internalStructure"
          :collectionByTab="internalStructureByTab"
        >
          <EditorDragItem
            v-for="(item, idx) in cleanSectionItems(section.items, internalStructure)"
            size="small"
            :key="`${item.name}-${idx}`"
            :item="item"
          />
        </drag-container>
      </div>
    </div>

    <div class="editor-column col-md-5 col-lg-5 col-xl-5">
      <drag-tab-container
        v-if="showTabs && tabsAvailable()"
        :tabs="internalTabs"
        :editorName="editorName"
      />
      <drag-container
        :class="getMainDragContainerClass(editorName)"
        @onDrop="onDrop"
        groupName="getGroupName()"
        :shouldRemoveOnDrop="true"
        :shouldAcceptDrops="true"
        :dragClass="dragClass"
        :showTabs="showTabs"
        :collection="internalStructure"
        :collectionByTab="internalStructureByTab"
      >
        <EditorDragItem
          v-for="(item, idx) in internalStructureItems()"
          :key="`${item.name}-${idx}`"
          size="big"
          :item="item"
          :setActiveOnClick="true"
          :class="getItemGrid(item, editorName)"
        />
        <div v-if="internalSubmitButton" class="row">
          <div class="col-12">
            <div
              class="drag-submit-button"
              :class="{'active': submitButtonIsActive}"
              @click="setSubmitActive"
            >
              <drag-submit-button
                :button="internalSubmitButton"
              />
            </div>
          </div>
        </div>
      </drag-container>
    </div>
    <div class="editor-column active-container col-md-4 col-lg-4 col-xl-4">
      <drag-active-item-container
        @delete="deleteElement"
        @edit="editElement"
        :editorName="editorName"
        :limitEditing="limitEditing"
      />
    </div>
  </div>
  <div
    v-else
  >
    <empty-state
      :heading="emptyStateOptions.heading"
      :subtitle="emptyStateOptions.subtitle"
    />
  </div>
</template>
<script>
import DragMixin from '@/components/mixins/Drag'
import EmptyState from '@/components/helpers/DataDisplay/EmptyState'
import { EventBus } from '@/EventBus.js'

export default {
  mixins: [
    DragMixin
  ],
  props: {
    limitEditing: {
      // Turning this to true stops the user from editing certain parameters of fields.
      // This is used in the lead sheet editor for example to limit editing to option values.
      type: Boolean,
      default: false
    },
    showTabs: {
      type: Boolean,
      default: false
    },
    editorName: {
      type: String
    }
  },
  components: {
    EmptyState
  },
  data() {
    return {
      dragClass: "drag-dragging",
      windowWidth: window.innerWidth,
      resizeInterval: null,
    }
  },
  computed: {
    emptyStateOptions() {
      return {
        heading: this.$t('ui.drag_editor.screenTooSmall.title'),
        subtitle: this.$t('ui.drag_editor.screenTooSmall.subtitle')
      }
    },
    submitButtonIsActive() {
      return this.$store.getters.getDragEditorActiveItem === this.internalSubmitButton
    },
    screenSizeIsTooSmall() {
      return this.windowWidth <= 766
    }
  },
  mounted() {
    this.$store.commit('SET_FOOTER_DISPLAY', false)
    EventBus.$on('action', this.handleAction)
    this.listenToSizeChanges()

    // NB! This is very important for the editor to work properly. If this is not done for some reason
    // the draggable part of the DragContainer will be skewed downwards and to the right - therefore
    // unaligning it with the editor itself. This means you have to drag items outside the boundary
    // of the editor in order to successfully drop them.
    // See: https://github.com/kutlugsahin/vue-smooth-dnd/issues/107
    this.resizeInterval = setInterval(() => {
      window.dispatchEvent(new Event('resize'))
    }, 500)
  },
  destroyed() {
    if (this.resizeInterval) {
      clearInterval(this.resizeInterval)
    }
  },
  methods: {
    getGroupName() {
      if (this.activeTab) {
        return this.activeTab.name
      } else {
        return "target"
      }
    },
    listenToSizeChanges() {
      window.onresize = () => {
        this.windowWidth  = window.innerWidth
      }
    },
    handleAction(payload) {
      let action    = payload.split('.')[1]
      let structure = [...this.internalStructure, ...this.internalTabs]

      // if submit button should be included, push here to the structure
      if (this.shouldIncludeSubmit) {
        structure.push(this.internalSubmitButton)
      }

      if (action === 'save') {
        this.$emit('save', structure)
      } else if (action === 'publish') {
        this.$emit('publish', structure)
      }
    },
    getItemGrid(item, editorName) {

      if (editorName == 'ExternalForm') {
        return 'col-12'
      } else {
        if (item.class) {
          return item.class
        } else {
          return "col-12"
        }
      }
    },
    setSubmitActive() {
      this.$store.commit('SET_DRAG_EDITOR_ACTIVE', this.internalSubmitButton)
    },
    internalStructureItems() {
      if (this.showTabs) {
        return this.internalStructureByTab
      } else {
        return this.internalStructure
      }
    },
    cleanSectionItems(sectionItems, structure) {

      let clean_section_items = []

      // here we filter all items that are UNIQUE in the Editor and are already present
      // isUniqueInCollection = item shall only exist one time on the Editor (usually a standard item like "firstname")
      // belongsToLeadSheet = used in FormEditor, it shall also only exist one time because its part of the LeadSheet
      sectionItems.forEach(function(item) {
        if (item.isUniqueInCollection || item.belongsToLeadSheet) {
          if (!structure.find(x => x.name === item.name)) {
            clean_section_items.push(item)
          }
        } else {
          clean_section_items.push(item)
        }
      });

      return clean_section_items
    },
    getMainDragContainerClass(editorName) {
      if (editorName == 'ExternalForm') {
        return "min-full-height-forms row gutter-xs"
      } else if (editorName == 'LeadMask') {
        return "min-full-height-lead-sheet row gutter-xs"
      } else {
        return "min-full-height row gutter-xs"
      }
    },
    tabsAvailable() {
      if (this.internalTabs && this.internalTabs.length > 0 && this.internalStructure.length > 0) {
        return true
      } else {
        return false
      }
    }
  },
  beforeDestroy() {
    this.$store.commit('SET_FOOTER_DISPLAY', true)
    this.$store.commit('SET_DRAG_EDITOR_ACTIVE', {})
  }
}
</script>

<style lang="sass" scoped>
.no-margin
  margin: 0 !important

// WARNING: this class should only affect FORMS

.min-full-height-forms
  // min-height: 100% !important

// WARNING: this class should only affect LEAD SHEETS

.min-full-height-lead-sheet
  padding-bottom: 190%
  padding-top: 20px
  border-top: 1px solid $paper

.drag-submit-button
  text-align: center
  border: 2px solid $paper-light
  border-radius: 5px
  padding: 15px
  cursor: pointer
  transition: .15s all ease-out
  &:active
    border: 2px solid $paper-dark
  &:hover
    border: 2px solid $paper-dark
.active
  border-color: $sensual
  background-color: $sensual-lighter
  transition: all .15s ease-out
  &:hover
    border-color: $sensual

.menu
  background-color: #f4f5f8

.editor
  position: absolute
  top: 121px
  bottom: 0
  left: 0
  right: 0

.editor-column
  overflow-y: scroll
  height: 100% !important
  padding: 30px 40px 30px
  h4
    margin: 0px 0px 20px 0px

.active-container
  box-shadow: -6px 0px 15px 3px #f4f5f8
</style>



// WEBPACK FOOTER //
// Editor.vue?19092c0a